import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { keyWhere, parseGoLink } from '../lib/deepLinks';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { useMetadataStore } from '@/entities/metadata/model/metadataStore';
import { AppHeader } from '../header/AppHeader';
import { SidebarLayout } from './SidebarLayout';
import { RailLayout } from './RailLayout';
import { TopNavLayout } from './TopNavLayout';
import { TopNavBar } from '../navMenu/TopNavBar';
import { TabBar } from '../tabs/TabBar';
import { TabPanes } from '../tabs/TabPanes';
import { useTabsStore } from '../tabs/tabsStore';
import { MobileDrawer } from '../mobile/MobileDrawer';
import { BottomBar } from '../mobile/BottomBar';
import { MobileTabsSheet } from '../mobile/MobileTabsSheet';
import { SpotlightDialog } from '../spotlight/SpotlightDialog';
import { TabSwitcher } from '../tabs/ui/TabSwitcher';
import { SettingsDialog } from '@/features/preferences/settingsDialog/SettingsDialog';
import { SessionExpiredDialog } from '@/features/auth/sessionExpired/SessionExpiredDialog';
import { AiHost } from '@/features/ai';
import { ChatHost } from '@/features/chat';
import { useShellStore } from '../model/shellStore';
import { useMenu } from '../navMenu/useMenu';
import { toast } from '@/shared/ui/toastStore';
import { useTranslation } from 'react-i18next';

/** Uygulama kabuğu: header + (sidebar|rail|top) + sekme çubuğu + keep-alive pane'ler; mobilde drawer + bottom bar. */
export function AppShell() {
  const { isMobile } = useBreakpoint();
  const menuPosition = usePrefsStore((s) => s.prefs.menuPosition);
  const tabsRestore = usePrefsStore((s) => s.prefs.tabsRestore);
  const [tabsOpen, setTabsOpen] = useState(false);
  const setSettings = useShellStore((s) => s.setSettings);
  const { t } = useTranslation();
  const { screenId } = useParams();
  const [search, setSearch] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { flat, loaded } = useMenu();
  const menuLoaded = useMetadataStore((s) => s.isMenuLoaded);

  // Sekmeleri geri yükle (bir kez)
  useEffect(() => {
    if (tabsRestore) useTabsStore.getState().hydrate();
  }, [tabsRestore]);

  // Derin link: /s/:screenId → sekme aç/odakla (menü yüklendikten sonra, yetkisiz ekran atlanır)
  useEffect(() => {
    if (!screenId || !menuLoaded) return;
    const st = useTabsStore.getState();
    const wanted = search.get('tab');
    // GUID karşılaştırması harf büyüklüğü gözetmez: bağlantı veritabanından ya da
    // masaüstünden kopyalandığında BÜYÜK harfli geliyor ve birebir eşleşme aramak
    // ekranı sessizce açmıyordu (kullanıcı ana sayfada kalıyordu).
    const sameScreen = (a: string | null | undefined) => !!a && a.toLowerCase() === screenId.toLowerCase();
    // Paylaşılan ekran (?f=): HER ZAMAN yeni sekme — alıcının açık sekmesi ve süzgeçleri bozulmasın.
    const shared = search.get('f');
    if (shared) {
      const node = flat.find((n) => sameScreen(n.screenId));
      const next = new URLSearchParams(search);
      next.delete('f');
      if (node) {
        const tab = st.openScreen(node, { shared }, { focusExisting: false });
        next.set('tab', tab.tabId);
      } else toast.warning(t('screen.noAccessTitle'), t('screen.noAccess'));
      setSearch(next, { replace: true });
      return;
    }
    const existing = st.tabs.find((t) => t.tabId === wanted) ?? st.tabs.find((t) => sameScreen(t.screenId));
    if (existing) {
      if (st.activeTabId !== existing.tabId) st.activate(existing.tabId);
      return;
    }
    const node = flat.find((n) => sameScreen(n.screenId));
    if (node) {
      st.openScreen(node);
      return;
    }
    /*
     * Menüde yok: ya ekran silinmiş ya da rolün yetkisi yok. Önceden hiçbir şey
     * olmuyordu — bağlantıyı açan kullanıcı boş ekrana bakıp "açılmadı mı, yavaş mı"
     * diye bekliyordu.
     */
    toast.warning(t('screen.noAccessTitle'), t('screen.noAccess'));
  }, [screenId, search, flat, loaded, menuLoaded]);

  // Bildirim bağlantısı: /go?s=…&o=…&kf=…&kv=… → ekranı o kayda süzülmüş açar (yetki menüden).
  useEffect(() => {
    if (location.pathname !== '/go' || !menuLoaded) return;
    const link = parseGoLink(location.search);
    const same = (a: string | null | undefined, b: string | null) =>
      !!a && !!b && a.toLowerCase() === b.toLowerCase();
    const node =
      flat.find((n) => same(n.screenId, link.screenId)) ?? flat.find((n) => same(n.objectId, link.objectId));
    if (!node) {
      toast.warning(t('screen.noAccessTitle'), t('screen.noAccess'));
      navigate('/', { replace: true });
      return;
    }
    const where = keyWhere(link.keyField, link.keyValue);
    const tab = useTabsStore
      .getState()
      .openScreen(node, where ? { whereClause: where } : {}, { focusExisting: !where });
    navigate(`/s/${node.screenId}?tab=${tab.tabId}`, { replace: true });
  }, [location.pathname, location.search, menuLoaded, flat, navigate, t]);

  const body = (
    <>
      <TabBar />
      <TabPanes />
    </>
  );

  return (
    <div className="bg-bg flex h-dvh flex-col overflow-hidden">
      <AppHeader center={!isMobile && menuPosition === 'top' ? <TopNavBar /> : null} />
      {isMobile ? (
        <>
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden">{body}</main>
          <BottomBar onTabs={() => setTabsOpen(true)} onProfile={() => setSettings(true)} />
          <MobileDrawer />
          <MobileTabsSheet open={tabsOpen} onOpenChange={setTabsOpen} />
        </>
      ) : menuPosition === 'rail' ? (
        <RailLayout>{body}</RailLayout>
      ) : menuPosition === 'top' ? (
        <TopNavLayout>{body}</TopNavLayout>
      ) : (
        <SidebarLayout>{body}</SidebarLayout>
      )}
      <SpotlightDialog />
      {/* Sekmeler arası klavyeyle geçiş: Alt+Q liste, Alt+PgUp/PgDn, Alt+1…9, Alt+W (#476). */}
      <TabSwitcher />
      <SettingsDialog />
      <SessionExpiredDialog />
      {/* AI asistanı: yetkisi ve sunucusu varsa düğme + sağdan açılan panel. */}
      <AiHost />
      <ChatHost />
    </div>
  );
}
